import type {
  QueryResult,
  BreakdownResult,
  CountResult,
  TrendResult,
  ListResult,
  PatientListItem,
  Dashboard,
  SavedCard,
  AnalyticsQuery,
} from '../types/analytics.types';

const MOCK_QUERY: AnalyticsQuery = {
  id: 'mock-query-1',
  title: 'Sample Query',
  filters: [],
  filterLogic: 'AND',
  output: { type: 'count' },
};

export const MOCK_COUNT_RESULT: QueryResult = {
  query: MOCK_QUERY,
  data: {
    type: 'count',
    count: 2847,
    comparisonCount: 2654,
    comparisonLabel: 'vs. last month',
  } as CountResult,
  meta: {
    totalCount: 2847,
    executionTimeMs: 156,
    generatedAt: new Date().toISOString(),
  },
};

export const MOCK_BREAKDOWN_RESULT: QueryResult = {
  query: { ...MOCK_QUERY, output: { type: 'breakdown', groupBy: 'care_manager' } },
  data: {
    type: 'breakdown',
    groups: [
      { key: 'sarah_chen', label: 'Sarah Chen', count: 312, percentage: 36.8 },
      { key: 'mike_johnson', label: 'Mike Johnson', count: 198, percentage: 23.4 },
      { key: 'lisa_park', label: 'Lisa Park', count: 167, percentage: 19.7 },
      { key: 'david_kim', label: 'David Kim', count: 112, percentage: 13.2 },
      { key: 'amy_wilson', label: 'Amy Wilson', count: 58, percentage: 6.8 },
    ],
    totalCount: 847,
  } as BreakdownResult,
  meta: {
    totalCount: 847,
    executionTimeMs: 234,
    generatedAt: new Date().toISOString(),
  },
};

export const MOCK_TREND_RESULT: QueryResult = {
  query: {
    ...MOCK_QUERY,
    output: {
      type: 'trend',
      timeRange: {
        start: '2024-07-01',
        end: '2024-12-31',
        granularity: 'month',
      },
    },
  },
  data: {
    type: 'trend',
    dataPoints: [
      { date: '2024-07-01', value: 72, label: 'Jul' },
      { date: '2024-08-01', value: 78, label: 'Aug' },
      { date: '2024-09-01', value: 81, label: 'Sep' },
      { date: '2024-10-01', value: 85, label: 'Oct' },
      { date: '2024-11-01', value: 89, label: 'Nov' },
      { date: '2024-12-01', value: 92, label: 'Dec' },
    ],
  } as TrendResult,
  meta: {
    totalCount: 847,
    executionTimeMs: 312,
    generatedAt: new Date().toISOString(),
  },
};

export const MOCK_PATIENT_LIST: PatientListItem[] = [
  {
    id: 'p1',
    mrn: 'MRN001234',
    firstName: 'John',
    lastName: 'Smith',
    dateOfBirth: '1955-03-15',
    age: 69,
    gender: 'Male',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Mike Johnson',
    payer: 'Medicare',
    riskScore: 2.4,
    phone: '5551234567',
    smsCapable: true,
  },
  {
    id: 'p2',
    mrn: 'MRN001235',
    firstName: 'Maria',
    lastName: 'Garcia',
    dateOfBirth: '1962-07-22',
    age: 62,
    gender: 'Female',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Lisa Park',
    payer: 'Blue Cross',
    riskScore: 1.8,
    phone: '5559876543',
    smsCapable: true,
  },
  {
    id: 'p3',
    mrn: 'MRN001236',
    firstName: 'Robert',
    lastName: 'Williams',
    dateOfBirth: '1948-11-30',
    age: 76,
    gender: 'Male',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Sarah Chen',
    payer: 'Medicare',
    riskScore: 3.2,
    phone: '5554567890',
    smsCapable: false,
  },
  {
    id: 'p4',
    mrn: 'MRN001237',
    firstName: 'Linda',
    lastName: 'Davis',
    dateOfBirth: '1970-05-08',
    age: 54,
    gender: 'Female',
    pcp: 'Dr. Lisa Park',
    careManager: 'David Kim',
    payer: 'Aetna',
    riskScore: 1.2,
    phone: '5553214567',
    smsCapable: true,
  },
  {
    id: 'p5',
    mrn: 'MRN001238',
    firstName: 'James',
    lastName: 'Wilson',
    dateOfBirth: '1958-09-12',
    age: 66,
    gender: 'Male',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Amy Wilson',
    payer: 'UnitedHealth',
    riskScore: 2.1,
    phone: '5557891234',
    smsCapable: true,
  },
  {
    id: 'p6',
    mrn: 'MRN001239',
    firstName: 'Patricia',
    lastName: 'Martinez',
    dateOfBirth: '1965-01-25',
    age: 60,
    gender: 'Female',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Mike Johnson',
    payer: 'Medicare',
    riskScore: 1.9,
    phone: '5556543210',
    smsCapable: true,
  },
  {
    id: 'p7',
    mrn: 'MRN001240',
    firstName: 'Michael',
    lastName: 'Brown',
    dateOfBirth: '1952-08-17',
    age: 72,
    gender: 'Male',
    pcp: 'Dr. Lisa Park',
    careManager: 'Lisa Park',
    payer: 'Blue Cross',
    riskScore: 2.8,
    phone: '5551112222',
    smsCapable: false,
  },
  {
    id: 'p8',
    mrn: 'MRN001241',
    firstName: 'Jennifer',
    lastName: 'Anderson',
    dateOfBirth: '1973-12-03',
    age: 51,
    gender: 'Female',
    pcp: 'Dr. Sarah Chen',
    careManager: 'David Kim',
    payer: 'Cigna',
    riskScore: 1.1,
    phone: '5553334444',
    smsCapable: true,
  },
  {
    id: 'p9',
    mrn: 'MRN001242',
    firstName: 'William',
    lastName: 'Taylor',
    dateOfBirth: '1960-04-28',
    age: 64,
    gender: 'Male',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Sarah Chen',
    payer: 'Medicare',
    riskScore: 2.3,
    phone: '5555556666',
    smsCapable: true,
  },
  {
    id: 'p10',
    mrn: 'MRN001243',
    firstName: 'Elizabeth',
    lastName: 'Thomas',
    dateOfBirth: '1967-06-14',
    age: 57,
    gender: 'Female',
    pcp: 'Dr. Lisa Park',
    careManager: 'Amy Wilson',
    payer: 'Aetna',
    riskScore: 1.5,
    phone: '5557778888',
    smsCapable: true,
  },
];

export const MOCK_LIST_RESULT: QueryResult = {
  query: { ...MOCK_QUERY, output: { type: 'list' } },
  data: {
    type: 'list',
    patients: MOCK_PATIENT_LIST,
    totalCount: 847,
    page: 1,
    pageSize: 10,
  } as ListResult,
  meta: {
    totalCount: 847,
    executionTimeMs: 189,
    generatedAt: new Date().toISOString(),
  },
};

export const MOCK_TCM_PATIENTS: PatientListItem[] = [
  {
    id: 'tcm1',
    mrn: 'MRN002001',
    firstName: 'Dorothy',
    lastName: 'Henderson',
    dateOfBirth: '1945-08-12',
    age: 80,
    gender: 'Female',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Mike Johnson',
    payer: 'Medicare',
    riskScore: 3.1,
    phone: '5552341001',
    smsCapable: true,
    dischargeDate: '2026-01-13',
    dischargeReason: 'CHF Exacerbation',
    daysUntilTCMDeadline: 4,
  },
  {
    id: 'tcm2',
    mrn: 'MRN002002',
    firstName: 'Harold',
    lastName: 'Patterson',
    dateOfBirth: '1950-03-22',
    age: 75,
    gender: 'Male',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Lisa Park',
    payer: 'Medicare',
    riskScore: 2.8,
    phone: '5552341002',
    smsCapable: false,
    dischargeDate: '2026-01-12',
    dischargeReason: 'Pneumonia',
    daysUntilTCMDeadline: 3,
  },
  {
    id: 'tcm3',
    mrn: 'MRN002003',
    firstName: 'Margaret',
    lastName: 'Collins',
    dateOfBirth: '1948-11-05',
    age: 77,
    gender: 'Female',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Sarah Chen',
    payer: 'Medicare',
    riskScore: 2.5,
    phone: '5552341003',
    smsCapable: true,
    dischargeDate: '2026-01-14',
    dischargeReason: 'Hip Replacement',
    daysUntilTCMDeadline: 5,
  },
  {
    id: 'tcm4',
    mrn: 'MRN002004',
    firstName: 'Eugene',
    lastName: 'Foster',
    dateOfBirth: '1952-06-30',
    age: 73,
    gender: 'Male',
    pcp: 'Dr. Lisa Park',
    careManager: 'David Kim',
    payer: 'Medicare',
    riskScore: 3.4,
    phone: '5552341004',
    smsCapable: true,
    dischargeDate: '2026-01-11',
    dischargeReason: 'COPD Exacerbation',
    daysUntilTCMDeadline: 2,
  },
  {
    id: 'tcm5',
    mrn: 'MRN002005',
    firstName: 'Betty',
    lastName: 'Richardson',
    dateOfBirth: '1946-02-18',
    age: 79,
    gender: 'Female',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Amy Wilson',
    payer: 'Medicare',
    riskScore: 2.9,
    phone: '5552341005',
    smsCapable: true,
    dischargeDate: '2026-01-15',
    dischargeReason: 'Diabetic Ketoacidosis',
    daysUntilTCMDeadline: 6,
  },
  {
    id: 'tcm6',
    mrn: 'MRN002006',
    firstName: 'Raymond',
    lastName: 'Morgan',
    dateOfBirth: '1949-09-08',
    age: 76,
    gender: 'Male',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Mike Johnson',
    payer: 'Medicare',
    riskScore: 3.0,
    phone: '5552341006',
    smsCapable: false,
    dischargeDate: '2026-01-10',
    dischargeReason: 'Acute MI',
    daysUntilTCMDeadline: 1,
  },
  {
    id: 'tcm7',
    mrn: 'MRN002007',
    firstName: 'Virginia',
    lastName: 'Brooks',
    dateOfBirth: '1951-12-25',
    age: 74,
    gender: 'Female',
    pcp: 'Dr. Lisa Park',
    careManager: 'Lisa Park',
    payer: 'Medicare',
    riskScore: 2.6,
    phone: '5552341007',
    smsCapable: true,
    dischargeDate: '2026-01-14',
    dischargeReason: 'Stroke',
    daysUntilTCMDeadline: 5,
  },
  {
    id: 'tcm8',
    mrn: 'MRN002008',
    firstName: 'Arthur',
    lastName: 'Price',
    dateOfBirth: '1947-04-14',
    age: 78,
    gender: 'Male',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Sarah Chen',
    payer: 'Medicare',
    riskScore: 3.2,
    phone: '5552341008',
    smsCapable: true,
    dischargeDate: '2026-01-12',
    dischargeReason: 'Sepsis',
    daysUntilTCMDeadline: 3,
  },
];

export const MOCK_HIGH_RISK_PATIENTS: PatientListItem[] = [
  {
    id: 'hr1',
    mrn: 'MRN003001',
    firstName: 'Walter',
    lastName: 'Hughes',
    dateOfBirth: '1947-05-20',
    age: 78,
    gender: 'Male',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Mike Johnson',
    payer: 'Medicare',
    riskScore: 3.8,
    phone: '5553451001',
    smsCapable: true,
    lastVisitDate: '2025-09-15',
    daysSinceVisit: 123,
    openGapCount: 4,
  },
  {
    id: 'hr2',
    mrn: 'MRN003002',
    firstName: 'Evelyn',
    lastName: 'Simmons',
    dateOfBirth: '1944-10-03',
    age: 81,
    gender: 'Female',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Lisa Park',
    payer: 'Medicare',
    riskScore: 3.5,
    phone: '5553451002',
    smsCapable: false,
    lastVisitDate: '2025-08-22',
    daysSinceVisit: 147,
    openGapCount: 5,
  },
  {
    id: 'hr3',
    mrn: 'MRN003003',
    firstName: 'George',
    lastName: 'Rivera',
    dateOfBirth: '1950-01-15',
    age: 76,
    gender: 'Male',
    pcp: 'Dr. Lisa Park',
    careManager: 'Sarah Chen',
    payer: 'Medicare',
    riskScore: 3.2,
    phone: '5553451003',
    smsCapable: true,
    lastVisitDate: '2025-10-01',
    daysSinceVisit: 107,
    openGapCount: 3,
  },
  {
    id: 'hr4',
    mrn: 'MRN003004',
    firstName: 'Helen',
    lastName: 'Campbell',
    dateOfBirth: '1946-07-28',
    age: 79,
    gender: 'Female',
    pcp: 'Dr. Sarah Chen',
    careManager: 'David Kim',
    payer: 'Medicare',
    riskScore: 3.6,
    phone: '5553451004',
    smsCapable: true,
    lastVisitDate: '2025-09-08',
    daysSinceVisit: 130,
    openGapCount: 6,
  },
  {
    id: 'hr5',
    mrn: 'MRN003005',
    firstName: 'Frank',
    lastName: 'Murphy',
    dateOfBirth: '1949-12-10',
    age: 76,
    gender: 'Male',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Amy Wilson',
    payer: 'Medicare',
    riskScore: 2.9,
    phone: '5553451005',
    smsCapable: true,
    lastVisitDate: '2025-10-05',
    daysSinceVisit: 103,
    openGapCount: 3,
  },
  {
    id: 'hr6',
    mrn: 'MRN003006',
    firstName: 'Ruth',
    lastName: 'Bailey',
    dateOfBirth: '1943-04-22',
    age: 82,
    gender: 'Female',
    pcp: 'Dr. Lisa Park',
    careManager: 'Mike Johnson',
    payer: 'Medicare',
    riskScore: 4.1,
    phone: '5553451006',
    smsCapable: false,
    lastVisitDate: '2025-07-30',
    daysSinceVisit: 170,
    openGapCount: 7,
  },
  {
    id: 'hr7',
    mrn: 'MRN003007',
    firstName: 'Carl',
    lastName: 'Cooper',
    dateOfBirth: '1948-08-05',
    age: 77,
    gender: 'Male',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Lisa Park',
    payer: 'Medicare',
    riskScore: 3.3,
    phone: '5553451007',
    smsCapable: true,
    lastVisitDate: '2025-09-20',
    daysSinceVisit: 118,
    openGapCount: 4,
  },
  {
    id: 'hr8',
    mrn: 'MRN003008',
    firstName: 'Florence',
    lastName: 'Reed',
    dateOfBirth: '1945-11-18',
    age: 80,
    gender: 'Female',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Sarah Chen',
    payer: 'Medicare',
    riskScore: 3.0,
    phone: '5553451008',
    smsCapable: true,
    lastVisitDate: '2025-10-10',
    daysSinceVisit: 98,
    openGapCount: 2,
  },
  {
    id: 'hr9',
    mrn: 'MRN003009',
    firstName: 'Albert',
    lastName: 'Kelly',
    dateOfBirth: '1951-03-07',
    age: 74,
    gender: 'Male',
    pcp: 'Dr. Lisa Park',
    careManager: 'David Kim',
    payer: 'Medicare',
    riskScore: 2.7,
    phone: '5553451009',
    smsCapable: true,
    lastVisitDate: '2025-09-28',
    daysSinceVisit: 110,
    openGapCount: 3,
  },
  {
    id: 'hr10',
    mrn: 'MRN003010',
    firstName: 'Mildred',
    lastName: 'Howard',
    dateOfBirth: '1942-09-14',
    age: 83,
    gender: 'Female',
    pcp: 'Dr. Sarah Chen',
    careManager: 'Amy Wilson',
    payer: 'Medicare',
    riskScore: 3.9,
    phone: '5553451010',
    smsCapable: false,
    lastVisitDate: '2025-08-05',
    daysSinceVisit: 164,
    openGapCount: 5,
  },
  {
    id: 'hr11',
    mrn: 'MRN003011',
    firstName: 'Ernest',
    lastName: 'Ward',
    dateOfBirth: '1947-06-25',
    age: 78,
    gender: 'Male',
    pcp: 'Dr. Mike Thompson',
    careManager: 'Mike Johnson',
    payer: 'Medicare',
    riskScore: 2.5,
    phone: '5553451011',
    smsCapable: true,
    lastVisitDate: '2025-10-08',
    daysSinceVisit: 100,
    openGapCount: 2,
  },
  {
    id: 'hr12',
    mrn: 'MRN003012',
    firstName: 'Gladys',
    lastName: 'Torres',
    dateOfBirth: '1944-02-28',
    age: 81,
    gender: 'Female',
    pcp: 'Dr. Lisa Park',
    careManager: 'Lisa Park',
    payer: 'Medicare',
    riskScore: 3.4,
    phone: '5553451012',
    smsCapable: true,
    lastVisitDate: '2025-09-12',
    daysSinceVisit: 126,
    openGapCount: 4,
  },
];

export const VBC_MOCK_RESULTS: Record<string, QueryResult> = {
  'vbc-apcm-eligible': {
    query: {
      id: 'vbc-apcm-eligible',
      title: 'APCM-Eligible Not Enrolled',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'count' },
    },
    data: {
      type: 'count',
      count: 847,
      subtitle: '$127,050/mo if enrolled',
    } as CountResult,
    meta: {
      totalCount: 847,
      executionTimeMs: 145,
      generatedAt: new Date().toISOString(),
    },
  },
  'vbc-awv-due': {
    query: {
      id: 'vbc-awv-due',
      title: 'AWV-Eligible No Visit YTD',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'count' },
    },
    data: {
      type: 'count',
      count: 1234,
      subtitle: '$185,100 revenue opportunity',
    } as CountResult,
    meta: {
      totalCount: 1234,
      executionTimeMs: 132,
      generatedAt: new Date().toISOString(),
    },
  },
  'vbc-gaps-closed': {
    query: {
      id: 'vbc-gaps-closed',
      title: 'Care Gaps Closed This Month',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'count' },
    },
    data: {
      type: 'count',
      count: 312,
      comparisonCount: 254,
      comparisonLabel: 'vs. last month',
      subtitle: 'gaps closed',
    } as CountResult,
    meta: {
      totalCount: 312,
      executionTimeMs: 118,
      generatedAt: new Date().toISOString(),
    },
  },
  'vbc-closure-trend': {
    query: {
      id: 'vbc-closure-trend',
      title: 'Care Gap Closure Rate (6mo)',
      filters: [],
      filterLogic: 'AND',
      output: {
        type: 'trend',
        timeRange: {
          start: '2025-08-01',
          end: '2026-01-31',
          granularity: 'month',
        },
      },
    },
    data: {
      type: 'trend',
      dataPoints: [
        { date: '2025-08-01', value: 68, label: 'Aug' },
        { date: '2025-09-01', value: 74, label: 'Sep' },
        { date: '2025-10-01', value: 78, label: 'Oct' },
        { date: '2025-11-01', value: 82, label: 'Nov' },
        { date: '2025-12-01', value: 86, label: 'Dec' },
        { date: '2026-01-01', value: 89, label: 'Jan' },
      ],
    } as TrendResult,
    meta: {
      totalCount: 89,
      executionTimeMs: 256,
      generatedAt: new Date().toISOString(),
    },
  },
  'vbc-ai-outreach': {
    query: {
      id: 'vbc-ai-outreach',
      title: 'AI Outreach Performance',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'breakdown', groupBy: 'care_manager' },
    },
    data: {
      type: 'breakdown',
      groups: [
        { key: 'calls', label: 'AI Calls Completed', count: 2847, percentage: 100 },
        { key: 'appointments', label: 'Appointments Scheduled', count: 847, percentage: 29.7 },
        { key: 'gaps_closed', label: 'Care Gaps Closed', count: 312, percentage: 11.0 },
      ],
      totalCount: 2847,
    } as BreakdownResult,
    meta: {
      totalCount: 2847,
      executionTimeMs: 178,
      generatedAt: new Date().toISOString(),
    },
  },
  'vbc-care-gaps-by-measure': {
    query: {
      id: 'vbc-care-gaps-by-measure',
      title: 'Open Care Gaps by Measure',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'breakdown', groupBy: 'care_gap_measure' },
    },
    data: {
      type: 'breakdown',
      groups: [
        { key: 'a1c', label: 'HbA1c Screening', count: 423, percentage: 26.4 },
        { key: 'mammogram', label: 'Breast Cancer Screening', count: 312, percentage: 19.5 },
        { key: 'awv', label: 'Annual Wellness Visit', count: 287, percentage: 17.9 },
        { key: 'colonoscopy', label: 'Colorectal Screening', count: 198, percentage: 12.4 },
        { key: 'eye_exam', label: 'Diabetic Eye Exam', count: 156, percentage: 9.7 },
        { key: 'nephropathy', label: 'Nephropathy Screening', count: 112, percentage: 7.0 },
        { key: 'flu', label: 'Flu Vaccination', count: 89, percentage: 5.6 },
        { key: 'pneumonia', label: 'Pneumonia Vaccination', count: 24, percentage: 1.5 },
      ],
      totalCount: 1601,
    } as BreakdownResult,
    meta: {
      totalCount: 1601,
      executionTimeMs: 198,
      generatedAt: new Date().toISOString(),
    },
  },
  'vbc-tcm-followups': {
    query: {
      id: 'vbc-tcm-followups',
      title: 'TCM Follow-ups This Week',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'list' },
    },
    data: {
      type: 'list',
      patients: MOCK_TCM_PATIENTS,
      totalCount: 8,
      page: 1,
      pageSize: 10,
    } as ListResult,
    meta: {
      totalCount: 8,
      executionTimeMs: 167,
      generatedAt: new Date().toISOString(),
    },
  },
  'vbc-high-risk-no-visit': {
    query: {
      id: 'vbc-high-risk-no-visit',
      title: 'High-Risk No Visit 90+ Days',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'list' },
    },
    data: {
      type: 'list',
      patients: MOCK_HIGH_RISK_PATIENTS,
      totalCount: 12,
      page: 1,
      pageSize: 12,
    } as ListResult,
    meta: {
      totalCount: 12,
      executionTimeMs: 189,
      generatedAt: new Date().toISOString(),
    },
  },
};

export const MOCK_DASHBOARDS: Dashboard[] = [
  {
    id: 'dash-1',
    name: 'VBC Command Center',
    isDefault: true,
    cards: [],
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'dash-2',
    name: 'Care Gap Tracking',
    isDefault: false,
    cards: [],
    createdAt: '2024-02-20T14:30:00Z',
    updatedAt: '2024-02-20T14:30:00Z',
  },
];

export const MOCK_SAVED_CARDS: SavedCard[] = [
  {
    id: 'vbc-apcm-eligible',
    title: 'APCM-Eligible Not Enrolled',
    query: {
      id: 'vbc-apcm-eligible',
      title: 'APCM-Eligible Not Enrolled',
      filters: [
        {
          id: 'f1',
          category: 'programs' as const,
          field: 'program_eligible_not_enrolled',
          operator: 'equals' as const,
          value: 'apcm',
        },
      ],
      filterLogic: 'AND',
      output: { type: 'count' },
    },
    visualizationType: 'big_number',
    dashboardId: 'dash-1',
    position: { x: 0, y: 0, w: 4, h: 2 },
    refreshFrequency: 'daily',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'vbc-awv-due',
    title: 'AWV-Eligible No Visit YTD',
    query: {
      id: 'vbc-awv-due',
      title: 'AWV-Eligible No Visit YTD',
      filters: [
        {
          id: 'f1',
          category: 'care_gaps' as const,
          field: 'care_gap_status',
          operator: 'equals' as const,
          value: 'annual_wellness',
        },
        {
          id: 'f2',
          category: 'care_gaps' as const,
          field: 'gap_is_open',
          operator: 'equals' as const,
          value: 'open',
        },
      ],
      filterLogic: 'AND',
      output: { type: 'count' },
    },
    visualizationType: 'big_number',
    dashboardId: 'dash-1',
    position: { x: 4, y: 0, w: 4, h: 2 },
    refreshFrequency: 'daily',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'vbc-gaps-closed',
    title: 'Care Gaps Closed This Month',
    query: {
      id: 'vbc-gaps-closed',
      title: 'Care Gaps Closed This Month',
      filters: [
        {
          id: 'f1',
          category: 'care_gaps' as const,
          field: 'gap_closed_date',
          operator: 'within_last_days' as const,
          value: 30,
        },
      ],
      filterLogic: 'AND',
      output: { type: 'count' },
    },
    visualizationType: 'big_number',
    dashboardId: 'dash-1',
    position: { x: 8, y: 0, w: 4, h: 2 },
    refreshFrequency: 'daily',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'vbc-closure-trend',
    title: 'Care Gap Closure Rate (6mo)',
    query: {
      id: 'vbc-closure-trend',
      title: 'Care Gap Closure Rate (6mo)',
      filters: [],
      filterLogic: 'AND',
      output: {
        type: 'trend',
        timeRange: {
          start: '2025-08-01',
          end: '2026-01-31',
          granularity: 'month',
        },
      },
    },
    visualizationType: 'line',
    dashboardId: 'dash-1',
    position: { x: 0, y: 2, w: 6, h: 4 },
    refreshFrequency: 'weekly',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'vbc-ai-outreach',
    title: 'AI Outreach Performance',
    query: {
      id: 'vbc-ai-outreach',
      title: 'AI Outreach Performance',
      filters: [],
      filterLogic: 'AND',
      output: { type: 'breakdown', groupBy: 'care_manager' },
    },
    visualizationType: 'horizontal_bar',
    dashboardId: 'dash-1',
    position: { x: 6, y: 2, w: 6, h: 4 },
    refreshFrequency: 'daily',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'vbc-care-gaps-by-measure',
    title: 'Open Care Gaps by Measure',
    query: {
      id: 'vbc-care-gaps-by-measure',
      title: 'Open Care Gaps by Measure',
      filters: [
        {
          id: 'f1',
          category: 'care_gaps' as const,
          field: 'gap_is_open',
          operator: 'equals' as const,
          value: 'open',
        },
      ],
      filterLogic: 'AND',
      output: { type: 'breakdown', groupBy: 'care_gap_measure' },
    },
    visualizationType: 'horizontal_bar',
    dashboardId: 'dash-1',
    position: { x: 0, y: 6, w: 4, h: 4 },
    refreshFrequency: 'daily',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'vbc-tcm-followups',
    title: 'TCM Follow-ups This Week',
    query: {
      id: 'vbc-tcm-followups',
      title: 'TCM Follow-ups This Week',
      filters: [
        {
          id: 'f1',
          category: 'utilization' as const,
          field: 'recent_discharge',
          operator: 'within_last_days' as const,
          value: 7,
        },
      ],
      filterLogic: 'AND',
      output: { type: 'list' },
    },
    visualizationType: 'table',
    dashboardId: 'dash-1',
    position: { x: 4, y: 6, w: 4, h: 4 },
    refreshFrequency: 'daily',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'vbc-high-risk-no-visit',
    title: 'High-Risk No Visit 90+ Days',
    query: {
      id: 'vbc-high-risk-no-visit',
      title: 'High-Risk No Visit 90+ Days',
      filters: [
        {
          id: 'f1',
          category: 'risk' as const,
          field: 'risk_tier',
          operator: 'in' as const,
          value: ['high', 'very_high'],
        },
        {
          id: 'f2',
          category: 'utilization' as const,
          field: 'last_visit_date',
          operator: 'more_than_days_ago' as const,
          value: 90,
        },
      ],
      filterLogic: 'AND',
      output: { type: 'list' },
    },
    visualizationType: 'table',
    dashboardId: 'dash-1',
    position: { x: 8, y: 6, w: 4, h: 4 },
    refreshFrequency: 'daily',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
];

export const SUGGESTED_QUERIES = [
  {
    text: 'How many diabetic patients have an open A1C gap?',
    query: {
      title: 'Diabetics with Open A1C Gap',
      filters: [
        {
          id: 'f1',
          category: 'conditions' as const,
          field: 'has_condition',
          operator: 'equals' as const,
          value: 'diabetes_type2',
        },
        {
          id: 'f2',
          category: 'care_gaps' as const,
          field: 'care_gap_status',
          operator: 'equals' as const,
          value: 'hba1c_screening',
        },
        {
          id: 'f3',
          category: 'care_gaps' as const,
          field: 'gap_is_open',
          operator: 'equals' as const,
          value: 'open',
        },
      ],
      filterLogic: 'AND' as const,
      output: { type: 'count' as const },
    },
  },
  {
    text: 'Show me patients with 3 or more ED visits in the last year',
    query: {
      title: 'High ED Utilizers',
      filters: [
        {
          id: 'f1',
          category: 'utilization' as const,
          field: 'ed_visits_12m',
          operator: 'greater_than_or_equals' as const,
          value: 3,
        },
      ],
      filterLogic: 'AND' as const,
      output: { type: 'list' as const },
    },
  },
  {
    text: 'Patients discharged this week without follow-up scheduled',
    query: {
      title: 'Recent Discharges Needing Follow-up',
      filters: [
        {
          id: 'f1',
          category: 'utilization' as const,
          field: 'recent_discharge',
          operator: 'within_last_days' as const,
          value: 7,
        },
      ],
      filterLogic: 'AND' as const,
      output: { type: 'list' as const },
    },
  },
  {
    text: 'A1C gap closure rate by month for the last 6 months',
    query: {
      title: 'A1C Gap Closure Trend',
      filters: [
        {
          id: 'f1',
          category: 'care_gaps' as const,
          field: 'care_gap_status',
          operator: 'equals' as const,
          value: 'hba1c_screening',
        },
      ],
      filterLogic: 'AND' as const,
      output: {
        type: 'trend' as const,
        timeRange: {
          start: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
          end: new Date().toISOString(),
          granularity: 'month' as const,
        },
      },
    },
  },
  {
    text: 'Which care manager has the most patients with uncontrolled diabetes?',
    query: {
      title: 'Uncontrolled Diabetics by Care Manager',
      filters: [
        {
          id: 'f1',
          category: 'conditions' as const,
          field: 'has_condition',
          operator: 'equals' as const,
          value: 'diabetes_type2',
        },
        {
          id: 'f2',
          category: 'conditions' as const,
          field: 'condition_status',
          operator: 'equals' as const,
          value: 'uncontrolled',
        },
      ],
      filterLogic: 'AND' as const,
      output: {
        type: 'breakdown' as const,
        groupBy: 'care_manager' as const,
      },
    },
  },
  {
    text: 'High risk patients not enrolled in CCM',
    query: {
      title: 'High Risk Not in CCM',
      filters: [
        {
          id: 'f1',
          category: 'risk' as const,
          field: 'risk_tier',
          operator: 'in' as const,
          value: ['high', 'very_high'],
        },
        {
          id: 'f2',
          category: 'programs' as const,
          field: 'program_eligible_not_enrolled',
          operator: 'equals' as const,
          value: 'ccm',
        },
      ],
      filterLogic: 'AND' as const,
      output: { type: 'list' as const },
    },
  },
  {
    text: 'Patients over 65 due for annual wellness visit',
    query: {
      title: 'AWV Due (65+)',
      filters: [
        {
          id: 'f1',
          category: 'demographics' as const,
          field: 'age',
          operator: 'greater_than_or_equals' as const,
          value: 65,
        },
        {
          id: 'f2',
          category: 'care_gaps' as const,
          field: 'care_gap_status',
          operator: 'equals' as const,
          value: 'annual_wellness',
        },
        {
          id: 'f3',
          category: 'care_gaps' as const,
          field: 'gap_is_open',
          operator: 'equals' as const,
          value: 'open',
        },
      ],
      filterLogic: 'AND' as const,
      output: { type: 'count' as const },
    },
  },
  {
    text: 'Breast cancer screening rate by payer',
    query: {
      title: 'Breast Cancer Screening by Payer',
      filters: [
        {
          id: 'f1',
          category: 'care_gaps' as const,
          field: 'care_gap_status',
          operator: 'equals' as const,
          value: 'breast_cancer_screening',
        },
      ],
      filterLogic: 'AND' as const,
      output: {
        type: 'breakdown' as const,
        groupBy: 'payer' as const,
      },
    },
  },
];

export const MOCK_FILTER_OPTIONS = {
  pcp: [
    { value: 'dr_sarah_chen', label: 'Dr. Sarah Chen' },
    { value: 'dr_mike_thompson', label: 'Dr. Mike Thompson' },
    { value: 'dr_lisa_park', label: 'Dr. Lisa Park' },
    { value: 'dr_david_kim', label: 'Dr. David Kim' },
  ],
  care_manager: [
    { value: 'sarah_chen', label: 'Sarah Chen' },
    { value: 'mike_johnson', label: 'Mike Johnson' },
    { value: 'lisa_park', label: 'Lisa Park' },
    { value: 'david_kim', label: 'David Kim' },
    { value: 'amy_wilson', label: 'Amy Wilson' },
  ],
  payer: [
    { value: 'medicare', label: 'Medicare' },
    { value: 'blue_cross', label: 'Blue Cross' },
    { value: 'aetna', label: 'Aetna' },
    { value: 'unitedhealth', label: 'UnitedHealth' },
    { value: 'cigna', label: 'Cigna' },
    { value: 'humana', label: 'Humana' },
  ],
};
